import React, { useEffect, useRef } from 'react';
import { EDataOperationPermission } from 'services/common/types';

/**
 * 权限控制组件(读写)
 */
interface AuthorityRwProps {
  children: React.ReactNode;
}
const AuthorityRw: React.FC<AuthorityRwProps> = ({ children }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handlePermission = (child: Element) => {
      // 递归查找所有子元素
      const processElement = (element: Element) => {
        const attributeValue = element.getAttribute('data-operation-permission');
        if (attributeValue) {
          if (!(attributeValue.includes(EDataOperationPermission.WRITE))) {
            // 只在首次处理时打印日志，避免重复
            if (!element.hasAttribute('data-processed')) {
              if (element instanceof Element) {
                element.setAttribute('data-processed', 'true');
                if (element instanceof SVGElement) {
                  element.setAttribute('style', 'cursor: not-allowed; opacity: 0.3;');
                } else if (element instanceof HTMLElement) {
                  element.style.cursor = 'not-allowed';
                  element.style.opacity = '0.3';
                }
                const clickHandler = (e: Event) => {
                  e.stopPropagation();
                };
                element.addEventListener('click', clickHandler);
                return clickHandler;
              }
            }
          }
        }
        // 递归处理所有子元素
        Array.from(element.children).forEach(processElement);
        return null;
      };
      return processElement(child);
    };
    const eventHandlers = new Map<Element, ((e: Event) => void) | null>();
    const observer = new MutationObserver(() => {
      if (ref.current) {
        Array.from(ref.current.children).forEach(child => {
          const oldHandler = eventHandlers.get(child);
          if (oldHandler) {
            child.removeEventListener('click', oldHandler);
          }
          const newHandler = handlePermission(child);
          eventHandlers.set(child, newHandler);
        });
      }
    });

    if (ref.current) {
      // 处理初始子元素
      Array.from(ref.current.children).forEach(child => {
        const handler = handlePermission(child);
        eventHandlers.set(child, handler);
      });

      observer.observe(ref.current, {
        childList: true,
        attributes: true,
        subtree: true, // 添加这个配置以观察所有后代元素
      });
    }

    return () => {
      // 清理所有事件监听器
      eventHandlers.forEach((handler, child) => {
        if (handler) {
          child.removeEventListener('click', handler);
        }
      });
      observer.disconnect();
    };
  }, []);
  return (
    <div ref={ref}>
      {children}
    </div>
  );
};

export default AuthorityRw;